import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, inject, signal } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { first } from 'rxjs/operators';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { WorkBook, WorkSheet, utils, writeFile } from 'xlsx';
import dayjs from 'dayjs';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';

import { ClarityModule, ClrLoadingState } from '@clr/angular';

import { FormsModule } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { SOTYP_Map } from 'src/app/models/api/SOTYP';
import { CommonModule } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-messregauswertungmodal',
  templateUrl: './messregauswertungmodal.component.html',
  styleUrls: ['./messregauswertungmodal.component.scss'],
  imports: [CommonModule, ClarityModule, FormsModule, ReactiveFormsModule, MrTranslatePipe, BackendLocaleDatePipe],
})
export class MessregAuswertungmodal {
  private apiService = inject(APIService);
  private mrTranslate = inject(MrTranslatePipe);
  protected localeDate = inject(BackendLocaleDatePipe);
  protected readonly objekte = toSignal<SOTYP_Map>(this.apiService.getSOTYP());
  protected readonly otypid = signal<number>(1);

  @Input() modalOpen: boolean = false;
  @Output() onOK: EventEmitter<any> = new EventEmitter<any>();

  @Output() modalOpenChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  inGroup: UntypedFormGroup = new UntypedFormGroup({
    datevon: new UntypedFormControl(BackendLocaleDatePipe.now),
    datebis: new UntypedFormControl(BackendLocaleDatePipe.now),
    anzeigeTyp: new UntypedFormControl(0),
  });

  public get von(): string {
    return this.inGroup.get('datevon').value;
  }
  public set von(value: string) {
    this.inGroup.get('datevon').setValue(value);
    value && this.getCounts();
  }
  public get bis(): string {
    return this.inGroup.get('datebis').value;
  }
  public set bis(value: string) {
    this.inGroup.get('datebis').setValue(value);
    value && this.getCounts();
  }

  isopened: boolean = false;
  objektlist: any;
  messlist: any;
  countmess: number = 0;
  countobjekte: number = 0;
  opruefid: any = [];

  open() {
    this.isopened = true;
    this.inGroup.controls.datevon.setValue(dayjs().startOf('year').format(BackendLocaleDatePipe.format));
    this.inGroup.controls.datebis.setValue(BackendLocaleDatePipe.now);

    this.getCounts();
    this.modalOpen = true;
  }

  onTypChange(value: number): void {
    this.getCounts();
  }

  async getCounts() {
    const { datevon, datebis, anzeigeTyp } = this.inGroup.value;
    if (!datevon || !datebis) return;

    const result = await firstValueFrom(
      this.apiService.getMessungAuswertung({
        status: 'getcount',
        start: datevon,
        ende: datebis,
        typ: anzeigeTyp,
        objekttyp: this.otypid(),
      })
    );

    if (result) {
      if (this.otypid() == 2) {
        this.countmess = result.countmess;
        this.countobjekte = result.countobjekte;
        this.opruefid = result.opruefids;
      } else {
        this.objektlist = result.objektlist;
        this.countmess = result.countmess;
        this.countobjekte = result.countobjekte;
        this.opruefid = result.objektlist.rows.map(item => item.OPRUEFID);
      }
    }
  }

  protected readonly startState = signal(ClrLoadingState.DEFAULT);

  onExport() {
    this.startState.set(ClrLoadingState.LOADING);
    let localLang = localStorage.getItem('language');
    let val = this.inGroup.value;
    let vals = {
      status: 'getmess',
      start: val.datevon,
      ende: val.datebis,
      typ: val.anzeigeTyp,
      opruefid: this.opruefid,
      lang: localLang,
      objekttyp: this.otypid(),
    };

    this.apiService
      .getMessungAuswertung(vals)
      .pipe(first())
      .subscribe((val: any) => {
        if (val) {
          this.messlist = val.messlist.rows;

          const fileName = this.mrTranslate.transform('Sammelauswertung') + '.xlsx';
          const ws: WorkSheet = utils.json_to_sheet(this.messlist);
          const wb: WorkBook = utils.book_new();
          utils.book_append_sheet(wb, ws, this.mrTranslate.transform('Sammelauswertung'));

          writeFile(wb, fileName);
          this.startState.set(ClrLoadingState.SUCCESS);
        } else this.startState.set(ClrLoadingState.DEFAULT);
      });
  }

  close() {
    this.modalOpen = false;
  }

  onSubmit() {
    this.onOK.emit('');
  }
}
